import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, ChevronDown, UserRound, Settings, LogOut, Flag, Lightbulb, Image as ImageIcon, X, Menu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { YaprioLogo } from "@/components/YaprioLogo";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/log";
import { useLanguage } from "@/context/LanguageContext";

export default function TopBar() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { subscribe } = useRealtime();
  const [unread, setUnread] = useState(0);
  const [feedbackKind, setFeedbackKind] = useState(null);
  const [feedbackCategory, setFeedbackCategory] = useState("other");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [feedbackImage, setFeedbackImage] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [ycBalance, setYcBalance] = useState(0);
  const initial = (user?.username || "?").charAt(0).toUpperCase();

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count);
    } catch (e) {
      logError("TopBar", e);
    }
  }, []);

  useEffect(() => { loadUnread(); api.get("/economy/wallet").then(({data}) => setYcBalance(data.balance || 0)).catch(() => {}); }, [loadUnread]);
  useEffect(() => subscribe((evt) => {
    // Only notification events can change this badge. Do not refetch on every realtime event.
    if (evt.type === "notification") loadUnread();
  }), [subscribe, loadUnread]);
  useEffect(() => {
    const handler = () => loadUnread();
    window.addEventListener("yaprio:refresh-unread", handler);
    return () => window.removeEventListener("yaprio:refresh-unread", handler);
  }, [loadUnread]);

  const goNotifications = () => {
    navigate("/notifications");
    setUnread(0);
  };

  const openFeedback = (kind) => { setFeedbackKind(kind); setFeedbackCategory("other"); setFeedbackDetails(""); setFeedbackImage(null); };
  const closeFeedback = () => { if (!feedbackBusy) setFeedbackKind(null); };
  const submitFeedback = async () => {
    if (!feedbackDetails.trim() && !feedbackImage) { toast.error(t("Add a description or a screenshot first.")); return; }
    setFeedbackBusy(true);
    try {
      const form = new FormData();
      form.append("category", feedbackCategory);
      form.append("details", feedbackDetails.trim());
      if (feedbackImage) form.append("image", feedbackImage);
      await api.post(`/site-feedback/${feedbackKind === "suggestion" ? "suggestions" : "reports"}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(feedbackKind === "suggestion" ? t("Suggestion sent — thank you!") : t("Report sent — thank you for helping improve Yaprio."));
      setFeedbackKind(null); setFeedbackDetails(""); setFeedbackImage(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFeedbackBusy(false); }
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") navigate("/discover");
  };

  return (
    <header className="sticky top-0 z-30 flex min-h-[64px] items-center gap-2 border-b border-slate-100 bg-white/90 px-3 py-2.5 shadow-[0_1px_12px_rgba(15,23,42,0.04)] backdrop-blur-xl dark:border-white/10 dark:bg-[#101116]/90 sm:gap-3 sm:px-6">
      <button
        type="button"
        onClick={() => window.dispatchEvent(new Event("yaprio:open-mobile-menu"))}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-700 transition hover:bg-pink-50 active:scale-95 focus-visible:ring-2 focus-visible:ring-pink-300 dark:text-white dark:hover:bg-white/10 lg:hidden"
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
        data-testid="mobile-menu-button"
      >
        <Menu className="h-6 w-6" strokeWidth={2.2} />
      </button>

      <button type="button" onClick={() => navigate("/discover")} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 sm:hidden" aria-label="Discover people" data-testid="mobile-search-button"><Search className="h-5 w-5" /></button>

      <div className="relative hidden max-w-md flex-1 sm:block">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder={t("Search users...")} data-testid="input-search-global" onFocus={() => navigate("/discover")} onKeyDown={handleSearchKey} className="h-11 rounded-full border-slate-100 bg-slate-50 pl-11 focus-visible:bg-white" />
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <button type="button" onClick={() => navigate("/wallet")} className="hidden h-9 items-center gap-1.5 rounded-full border border-pink-100 bg-pink-50 px-3 text-xs font-bold text-pink-600 transition hover:border-pink-200 hover:bg-pink-100 sm:flex" data-testid="btn-wallet"><span>🪙</span>{new Intl.NumberFormat("en-US").format(ycBalance)} YC</button>
        <button type="button" onClick={() => openFeedback("report")} className="flex h-9 w-9 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-600 transition hover:border-pink-200 hover:bg-pink-50 hover:text-pink-600 sm:w-auto sm:px-3" data-testid="btn-report-site"><Flag className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Report")}</span></button>
        <button type="button" onClick={() => openFeedback("suggestion")} className="flex h-9 w-9 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-600 transition hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600 sm:w-auto sm:px-3" data-testid="btn-suggest-site"><Lightbulb className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("Suggest")}</span></button>
        <button onClick={goNotifications} className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 active:scale-95" data-testid="btn-notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-pink-500 px-1 text-[10px] font-bold text-white" data-testid="notification-badge">{unread > 9 ? "9+" : unread}</span>}
        </button>
        <div className="relative">
          <DropdownMenu dir="ltr">
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-1.5 transition-colors hover:bg-slate-100 sm:pr-2" data-testid="btn-user-menu">
                <Avatar className="h-8 w-8"><AvatarImage src={mediaUrl(user?.avatar_url)} alt={user?.username} /><AvatarFallback className="bg-pink-500 text-white text-sm font-bold">{initial}</AvatarFallback></Avatar>
                <span className="hidden text-sm font-semibold text-slate-700 sm:inline">{user?.username || "you"}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent portal={false} side="bottom" align="end" sideOffset={8} avoidCollisions={false} className="!absolute !left-auto !right-0 !top-[calc(100%+8px)] !transform-none w-56 rounded-2xl border-slate-200/80 bg-white/95 p-1.5 shadow-xl shadow-slate-900/10 backdrop-blur-xl dark:border-white/10 dark:bg-[#18181d]/95">
            <DropdownMenuItem
              onClick={() => navigate("/profile")}
              data-testid="menu-profile"
              className="cursor-pointer gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold"
            >
              <UserRound className="h-4 w-4 text-slate-500" />
              <span>{t("My Profile")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              data-testid="menu-settings"
              className="cursor-pointer gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold"
            >
              <Settings className="h-4 w-4 text-slate-500" />
              <span>{t("Settings")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1.5" />
            <DropdownMenuItem
              className="cursor-pointer gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-500 focus:text-red-500"
              data-testid="menu-logout"
              onClick={async () => { await logout(); toast("Signed out"); navigate("/", { replace: true }); }}
            >
              <LogOut className="h-4 w-4" />
              <span>{t("Sign out")}</span>
            </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Dialog open={!!feedbackKind} onOpenChange={(open) => { if (!open) closeFeedback(); }}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogTitle className="flex items-center gap-2 font-heading text-lg font-extrabold text-slate-900">
            {feedbackKind === "suggestion" ? <Lightbulb className="h-5 w-5 text-purple-500" /> : <Flag className="h-5 w-5 text-pink-500" />}
            {feedbackKind === "suggestion" ? t("Suggest an improvement") : t("Report a site issue")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {feedbackKind === "suggestion" ? t("Tell us what you'd like to see on Yaprio. You can attach a screenshot too.") : t("Tell us what went wrong on Yaprio. A screenshot helps us reproduce the issue.")}
          </DialogDescription>
          <div className="space-y-3">
            <select value={feedbackCategory} onChange={(e) => setFeedbackCategory(e.target.value)} className="h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none focus:border-pink-300">
              {(feedbackKind === "suggestion" ? [["feature","New feature"],["improvement","Improvement"],["design","Design / UI"],["other","Other"]] : [["bug","Bug / broken feature"],["ui","UI / layout issue"],["performance","Performance"],["security","Security concern"],["other","Other"]]).map(([value,label]) => <option key={value} value={value}>{t(label)}</option>)}
            </select>
            <Textarea value={feedbackDetails} onChange={(e) => setFeedbackDetails(e.target.value)} maxLength={3000} placeholder={feedbackKind === "suggestion" ? t("What should we improve or add?") : t("What happened? Tell us what you expected and what went wrong.")} className="min-h-32 rounded-2xl" />
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3">
              <input id="site-feedback-image" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => setFeedbackImage(e.target.files?.[0] || null)} />
              {!feedbackImage ? <label htmlFor="site-feedback-image" className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-sm font-semibold text-slate-600 hover:bg-white"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-pink-500 shadow-sm"><ImageIcon className="h-4 w-4" /></span><span>{t("Add a screenshot / picture")} <span className="font-normal text-slate-400">{t("(optional)")}</span></span></label> : <div className="flex items-center gap-3"><img src={URL.createObjectURL(feedbackImage)} alt="Selected screenshot" className="h-20 w-20 rounded-xl object-cover" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-slate-700">{feedbackImage.name}</p><p className="text-xs text-slate-400">{t("Ready to upload")}</p></div><button type="button" onClick={() => setFeedbackImage(null)} className="rounded-full p-2 text-slate-400 hover:bg-white hover:text-red-500" aria-label={t("Remove picture")}><X className="h-4 w-4" /></button></div>}
            </div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={closeFeedback} disabled={feedbackBusy} className="rounded-full">{t("Cancel")}</Button><Button type="button" onClick={submitFeedback} disabled={feedbackBusy} className="rounded-full bg-pink-500 text-white hover:bg-pink-600">{feedbackBusy ? t("Sending…") : feedbackKind === "suggestion" ? t("Send suggestion") : t("Send report")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
